import { BeneficiaryBankResponseDto } from './../model/beneficiary/beneficiary-bank.dto';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { GenericResponseDto } from '../model/common/generic-response.dto';
import { PageResponseDto } from '../model/common/page-response.dto';
import { BeneficiaryDocumentTypeResponseDto } from '../model/beneficiary/beneficiary-document-type.dto';
import { BeneficiaryAccountTypeResponseDto } from '../model/beneficiary/beneficiary-account-type.dto';
import { BeneficiaryCreateRequestDto, BeneficiaryCreateResponseDto, BeneficiaryUpdateRequestDto } from '../model/beneficiary/beneficiary-create.dto';
import { BeneficiaryFilterDto, BeneficiaryResponseDto } from '../model/beneficiary/beneficiary.dto';
import { HttpParamsUtil } from '@app/shared/utils/http-params.util';

@Injectable({
  providedIn: 'root'
})
export class BeneficiaryService {

  private apiUrl = environment.apiBaseUrl;
  private readonly skipErrorToastHeaders = new HttpHeaders({ 'X-Skip-Error-Toast': 'true' });

  constructor(private http: HttpClient) { }

  createBeneficiary(requestDto: BeneficiaryCreateRequestDto): Observable<GenericResponseDto<BeneficiaryCreateResponseDto>> {
    return this.http.post<GenericResponseDto<BeneficiaryCreateResponseDto>>(
      `${this.apiUrl}${environment.services.beneficiary.create}`,
      requestDto,
      { headers: this.skipErrorToastHeaders }
    );
  }

  updateBeneficiary(id: string, requestDto: BeneficiaryUpdateRequestDto): Observable<GenericResponseDto<void>> {
    return this.http.put<GenericResponseDto<void>>(
      `${this.apiUrl}${environment.services.beneficiary.updateById(id)}`,
      requestDto,
      { headers: this.skipErrorToastHeaders }
    );
  }

  deleteBeneficiary(id: string): Observable<GenericResponseDto<void>> {
    return this.http.delete<GenericResponseDto<void>>(
      `${this.apiUrl}${environment.services.beneficiary.deleteById(id)}`
    );
  }

  getBeneficiaryById(id: string): Observable<GenericResponseDto<BeneficiaryResponseDto>> {
    return this.http.get<GenericResponseDto<BeneficiaryResponseDto>>(
      `${this.apiUrl}${environment.services.beneficiary.findById(id)}`
    );
  }

  getAllBeneficiaries(): Observable<GenericResponseDto<BeneficiaryResponseDto[]>> {
    return this.http.get<GenericResponseDto<BeneficiaryResponseDto[]>>(
      `${this.apiUrl}${environment.services.beneficiary.findAll}`
    );
  }

  page(filter: BeneficiaryFilterDto): Observable<GenericResponseDto<PageResponseDto<BeneficiaryResponseDto>>> {
    const params = HttpParamsUtil.buildParams(filter);
    return this.http.get<GenericResponseDto<PageResponseDto<BeneficiaryResponseDto>>>(
      `${this.apiUrl}${environment.services.beneficiary.page}`,
      { params }
    );
  }

  getBeneficiaryByDocumentNumber(documentNumber: string): Observable<GenericResponseDto<BeneficiaryResponseDto>> {
    return this.http.get<GenericResponseDto<BeneficiaryResponseDto>>(
      `${this.apiUrl}${environment.services.beneficiary.findByDocumentNumber(documentNumber)}`
    );
  }

  isDocumentBlacklisted(documentTypeId: string, documentNumber: string): Observable<GenericResponseDto<boolean>> {
    return this.http.get<GenericResponseDto<boolean>>(
      `${this.apiUrl}/beneficiary/blacklist-check`,
      { params: { documentTypeId, documentNumber }, headers: this.skipErrorToastHeaders }
    );
  }

  getDocumentTypes(): Observable<GenericResponseDto<BeneficiaryDocumentTypeResponseDto[]>> {
    return this.http.get<GenericResponseDto<BeneficiaryDocumentTypeResponseDto[]>>(
      `${this.apiUrl}${environment.services.beneficiary.codes.documentTypes}`
    );
  }

  getBanks(): Observable<GenericResponseDto<BeneficiaryBankResponseDto[]>> {
    return this.http.get<GenericResponseDto<BeneficiaryBankResponseDto[]>>(
      `${this.apiUrl}${environment.services.beneficiary.codes.banks}`
    );
  }

  getBankAccountTypes(): Observable<GenericResponseDto<BeneficiaryAccountTypeResponseDto[]>> {
    return this.http.get<GenericResponseDto<BeneficiaryAccountTypeResponseDto[]>>(
      `${this.apiUrl}${environment.services.beneficiary.codes.bankAccountTypes}`
    );
  }

  exportExcelBeneficiaries(filter?: BeneficiaryFilterDto): Observable<HttpResponse<Blob>> {
    const params = HttpParamsUtil.buildParams(filter || {});
    return this.http.post(`${this.apiUrl}${environment.services.beneficiary.exportExcelBeneficiaries}`, null, {
      params,
      responseType: 'blob',
      observe: 'response'
    });
  }
}
